import { Fragment, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { isAfter, startOfDay, startOfMonth, subDays } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { InvoiceDisplayStatus } from 'anchor-scout-common/types';
import { Divider, Spinner, TableList, TextButton, Typography, WithInfoIcon } from 'seagull';
import GenericError from "../../../components/GenericError/GenericError";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../../../constants";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { useLocaleKeys } from "../../../i18n";
import { MoneyModel, PayoutSummaryStatus, PayoutsSummaryModel, PayoutsSummaryPayoutModel } from "../../../models";
import { selectors } from "../../../store/state-selectors";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import GraphWidgetTopCard from "../GraphWidget/GraphWidgetTopCard";
import styles from "./PayoutsSummaryWidget.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const renderDivider = () => /*#__PURE__*/_jsx(Divider, {
  margin: 0
});
const renderCell = (value, testid) => ({
  value: /*#__PURE__*/_jsx(Typography, {
    "data-testid": testid,
    children: value
  })
});
const renderHeaderItem = value => ({
  value: /*#__PURE__*/_jsx(Typography, {
    weight: "normal",
    children: value
  })
});
function getTableData() {
  const config = [{
    columnDef: {
      width: [100, 200]
    },
    headerItem: renderHeaderItem('Date')
  }, {
    columnDef: {
      width: [100, 200],
      hAlign: 'end'
    },
    headerItem: renderHeaderItem('Amount')
  }, {
    columnDef: {
      width: 80,
      hAlign: 'end'
    },
    headerItem: renderHeaderItem('Status')
  }];
  return {
    columnDefs: config.map(i => i.columnDef),
    headerItems: config.map(i => i.headerItem)
  };
}
const PayoutsSummaryTable = _ref => {
  let {
    data,
    testid
  } = _ref;
  const {
    columnDefs,
    headerItems
  } = useMemo(() => getTableData(), []);
  return /*#__PURE__*/_jsx("div", {
    className: styles.table,
    "data-testid": testid,
    children: /*#__PURE__*/_jsxs(TableList.Provider, {
      variant: "minimal",
      columnDefs: columnDefs,
      hPadding: "xs",
      children: [/*#__PURE__*/_jsx(TableList.Header, {
        items: headerItems,
        bgColor: "transparent",
        size: "small"
      }), renderDivider(), data.mapPayouts((payout, idx, isLast) => /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(TableList.Row, {
          disableMarginBottom: true,
          highlightOnHover: true,
          size: "medium",
          as: NavLink,
          to: payout.redirectURL(),
          items: compact([renderCell(payout.formattedDate(), "payout-summary-date-".concat(idx)), renderCell(payout.formattedAmount(), "payout-summary-amount-".concat(idx)), renderCell(payout.statusText(), "payout-summary-status-".concat(idx))])
        }), !isLast && renderDivider()]
      }, payout.uniqueKey())), /*#__PURE__*/_jsxs("div", {
        className: styles.viewMore,
        children: [/*#__PURE__*/_jsx(TextButton, {
          as: NavLink,
          to: routes.payoutsDashboard,
          "data-testid": "payouts-summary-view-more",
          underline: "none",
          weight: "normal",
          children: "View more"
        }), /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph3",
          skin: "secondary",
          children: "* Future payouts may vary in amount and date"
        })]
      })]
    })
  });
};
export function getEmptyData(now) {
  const firstOfMonth = startOfMonth(now);
  let dateToAdd = startOfDay(now);
  const payouts = [];
  while (!isAfter(firstOfMonth, dateToAdd) && payouts.length < 5) {
    payouts.push(new PayoutsSummaryPayoutModel({
      id: '',
      date: dateToAdd,
      amount: new MoneyModel(new Decimal(1200)),
      status: PayoutSummaryStatus.Paid
    }));
    dateToAdd = subDays(dateToAdd, 1);
  }
  return PayoutsSummaryModel.makeEmpty().addPayouts(payouts);
}
function PlaceholderTable() {
  const {
    getNow
  } = useIoContext();
  const emptyData = useMemo(() => getEmptyData(getNow()), []);
  return /*#__PURE__*/_jsx(PayoutsSummaryTable, {
    testid: "payouts-summary-table-placeholder",
    data: emptyData
  });
}
const TopCard = _ref2 => {
  let {
    testid,
    loading,
    label,
    info,
    value,
    linkTo
  } = _ref2;
  return /*#__PURE__*/_jsx(GraphWidgetTopCard, {
    linkTo: linkTo,
    "data-testid": loading ? "".concat(testid, "-loading") : testid,
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.topCard,
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(WithInfoIcon, {
          info: info,
          children: /*#__PURE__*/_jsx(Typography, {
            children: label
          })
        })
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h5",
        weight: "normal",
        "data-testid": "".concat(testid, "-value"),
        children: loading ? /*#__PURE__*/_jsx(Spinner, {
          size: 30,
          "data-testid": "".concat(testid, "-loading-spinner")
        }) : value
      })]
    })
  });
};
function getFuturePayoutsLink() {
  const filters = {
    status: [InvoiceDisplayStatus.PaymentInitiated]
  };
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    params.set(key, filters[key].join(','));
  });
  return "".concat(routes.invoicesDashboard, "?").concat(params);
}
function PayoutsSummaryWidgetContent(_ref3) {
  let {
    loading,
    data
  } = _ref3;
  const {
    t
  } = useLocaleKeys('pages.dashboard.payoutsSummaryWidget');
  const noData = !data;
  const topCards = [/*#__PURE__*/_jsx(TopCard, {
    testid: "payouts-summary-paid-this-month",
    loading: noData,
    label: "Paid this month",
    info: "Funds transferred to your bank account this month via Anchor.",
    value: data ? data.formattedPaidThisMonth() : '',
    linkTo: routes.payoutsDashboard
  }, "paid"), /*#__PURE__*/_jsx(TopCard, {
    testid: "payouts-summary-future-payouts",
    loading: noData,
    label: "Processing",
    info: "Funds still in transit from your clients' accounts. This process can take up to 3 business days and may be affected by refunds or payment failures.",
    value: data ? data.formattedFuturePayouts() : '',
    linkTo: getFuturePayoutsLink()
  }, "future")];
  return /*#__PURE__*/_jsxs(GraphWidgetLayout, {
    title: t.title(),
    loading: loading,
    bottomHeight: "l",
    children: [topCards, /*#__PURE__*/_jsx(GraphWidgetBottomCard, {
      "data-testid": "payouts-summary",
      loading: noData,
      loadingMsg: "Loading data...",
      empty: data && data.isEmpty(),
      emptyMsg: "Here you will view your payouts",
      loadingOrEmptyBg: () => /*#__PURE__*/_jsx(PlaceholderTable, {}),
      children: data && /*#__PURE__*/_jsx(PayoutsSummaryTable, {
        testid: "payouts-summary-table",
        data: data
      })
    })]
  });
}
export default function PayoutsSummaryWidget() {
  const {
    fetchPayoutsSummary
  } = useStoreActions();
  const initialData = useStore(state => state.payoutsSummary.data);
  const isMemberUser = useStore(selectors.user.isMemberUser);
  return isMemberUser ? null : /*#__PURE__*/_jsx(RestfulWrapper, {
    initialData: initialData,
    fetch: fetchPayoutsSummary,
    renderChild: _ref4 => {
      let {
        data,
        loading
      } = _ref4;
      return /*#__PURE__*/_jsx(PayoutsSummaryWidgetContent, {
        data: data,
        loading: loading
      });
    },
    renderLoader: () => /*#__PURE__*/_jsx(PayoutsSummaryWidgetContent, {
      loading: true
    }),
    renderError: () => /*#__PURE__*/_jsx(GenericError, {
      testid: "widget-error"
    })
  });
}